// Sign in with Apple JS — the WEB counterpart of signInWithAppleNative()
// (utils/nativeSocialLogin.js). Same job, different SDK: native uses the capgo
// Capacitor plugin; the web uses Apple's browser SDK against the "Petzy Web"
// Services ID. Both hand the SAME shape to AppleSignInButton, so the button and
// every page handler are unchanged.
//
// Loaded ON DEMAND, from inside signInWithAppleWeb(), not via a <script> tag in
// index.html. Google's static tag (index.html) is a wart, not a precedent: it
// puts a third-party CDN on the critical path of every page in the SPA shell,
// and it's why every caller carries a poll-for-the-global loop. On-demand
// injection returns a promise the click handler awaits, so a CDN failure can
// only reject inside the button's own promise — never break the login page.
//
// Cannot be tested locally: Apple rejects localhost as a Return URL, so this
// path only runs on petzy.ro. Its failure mode is "the Apple button errors",
// never "the page breaks" — the env flag below is what guarantees that.

import { isNativeApp } from './platform';

// The Services ID identifier from the Apple portal (com.mariusnpop.petzy.web).
// This is the client_id Apple stamps into the token's `aud`; the backend accepts
// it only when APPLE_WEB_CLIENT_ID is also set on Railway (config/appleClients.js).
const SERVICES_ID = import.meta.env.VITE_APPLE_SERVICES_ID || '';

// Must match, byte for byte, the Return URL registered on the Services ID AND
// the backend's APPLE_WEB_REDIRECT_URI. Hardcoded on purpose: Apple validates it
// against the one registered URL, so it can't follow window.location.origin (a
// vercel-preview origin isn't registered and would be rejected). Web Apple
// sign-in works on petzy.ro only, by design.
const REDIRECT_URI = 'https://petzy.ro/login';

// The button shows ONLY when configured. No VITE_APPLE_SERVICES_ID → no button →
// the auth pages are byte-identical to today. Since this can't be tested
// locally, the flag IS the insurance: a missing/wrong config yields "no button",
// never "the most prominent button on the page fails for everyone".
export const APPLE_WEB_SIGNIN_ENABLED = !isNativeApp && Boolean(SERVICES_ID);

// Apple's SDK. The locale segment only affects Apple's own popup chrome (which
// follows the user's Apple ID language anyway) — we render our own button and
// call signIn() directly, so it's cosmetic.
const SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

let sdkPromise = null;
let initialized = false;

function loadSdk() {
  if (window.AppleID?.auth) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      // Don't cache a rejected load: a transient CDN blip would otherwise
      // poison every later tap. Clearing it lets the next click retry.
      sdkPromise = null;
      reject(new Error('Apple sign-in SDK failed to load'));
    };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

// AppleID.auth.init is a window singleton (like GIS): calling it twice is
// undefined behaviour, so guard to exactly once per page load.
function ensureInit() {
  if (initialized) return;
  window.AppleID.auth.init({
    clientId: SERVICES_ID,
    // 'name email' is what makes Apple return the user's name — but ONLY on the
    // first-ever authorization for this Apple ID + primary-App-ID group. With
    // usePopup the SDK handles the form_post response internally and resolves
    // signIn() with the user object; later sign-ins resolve without it, which
    // the backend handles (existing user → name untouched).
    scope: 'name email',
    redirectURI: REDIRECT_URI,
    usePopup: true,
  });
  initialized = true;
}

// Drive the web Sign in with Apple. Returns { identityToken, fullName,
// authorizationCode } — the exact shape signInWithAppleNative() returns, so
// AppleSignInButton's onAuthenticate contract is identical across platforms.
export async function signInWithAppleWeb() {
  if (isNativeApp) throw new Error('Web Apple sign-in invoked on native.');
  if (!SERVICES_ID) throw new Error('Apple web sign-in is not configured.');
  await loadSdk();
  ensureInit();

  // Popup mode resolves with { authorization: { id_token, code, state }, user? }.
  // A user-cancel REJECTS with error 'popup_closed_by_user' — the button's
  // cancel regex swallows it.
  const res = await window.AppleID.auth.signIn();

  const identityToken = res?.authorization?.id_token;
  if (!identityToken) {
    throw new Error('Apple nu a returnat un token de identitate.');
  }
  const authorizationCode = res?.authorization?.code || null;

  // `user` is present only on the first authorization ever. Flatten name → the
  // same string|null the native path produces.
  const name = res?.user?.name;
  const fullName = name
    ? [name.firstName, name.lastName].filter(Boolean).join(' ') || null
    : null;

  return { identityToken, fullName, authorizationCode };
}
